import {
  Button,
  IconButton,
  Input,
  MenuContainer,
  MenuGroup,
  MenuInfo,
  MenuItem,
  MenuSeparator,
  ResponsiveDialog,
  Text,
  useToast,
  zero,
} from "@streamplace/components";
import { usePDSAgent } from "@streamplace/components/src/streamplace-store/xrpc";
import { EmptyState, EmptyStateTile } from "components/empty-state";
import Loading from "components/loading/loading";
import {
  Check,
  GripVertical,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react-native";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, ScrollView, View } from "react-native";
import Sortable from "react-native-sortables";
import { place } from "streamplace";
import { SettingsRowItem } from "./components/settings-navigation-item";
import { SettingsViewHeader } from "./components/settings-view-header";

const { text, mt, mb, px, py, w, layout, gap, r, p } = zero;

interface ActorSearchResult {
  did: string;
  handle: string;
}

export default function RecommendationsManager() {
  const agent = usePDSAgent();
  const { theme } = zero.useTheme();
  const [streamers, setStreamers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    isVisible: boolean;
    index: number | null;
  }>({ isVisible: false, index: null });
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState("");

  const a = useToast();

  const [activeDrag, setActiveDrag] = useState("");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<ActorSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchDebounceTimeout, setSearchDebounceTimeout] =
    useState<NodeJS.Timeout | null>(null);

  const { t } = useTranslation("settings");

  const loadRecommendations = async () => {
    if (!agent) return;

    try {
      setLoading(true);
      const userDID = agent.did;
      if (!userDID) {
        setStreamers([]);
        return;
      }

      // Get the record directly from the PDS for editing
      // todo: type this right
      let record: any;
      try {
        const { value } = await agent.client.get(
          place.stream.live.recommendations,
        );
        record = value;
      } catch {
        // Create a new empty record if not found
        await agent.client.create(place.stream.live.recommendations, {
          streamers: [],
          createdAt: new Date().toISOString(),
        } as any);
        record = { streamers: [] };
      }
      setStreamers(record.streamers || []);
    } catch (error: any) {
      console.error("Failed to load recommendations:", error);
      if (error.status !== 404) {
        a.show("Error", "Failed to load recommendations. Please try again.");
      }
      setStreamers([]);
    } finally {
      setLoading(false);
    }
  };

  const saveRecommendations = async (newStreamers: string[]) => {
    if (!agent || saving) return;

    try {
      if (!agent.did) {
        throw new Error("User DID not found");
      }
      setSaving(true);

      // Use put to create or update the record
      await agent.client.put(place.stream.live.recommendations, {
        streamers: newStreamers,
        createdAt: new Date().toISOString(),
      } as any);

      setStreamers(newStreamers);
    } catch (error: any) {
      console.error("Failed to save recommendations:", error);
      a.show(
        "Error",
        error.message || "Failed to save recommendations. Please try again.",
      );
      // Reload to get back to consistent state
      await loadRecommendations();
    } finally {
      setSaving(false);
    }
  };

  const searchActors = useCallback(
    async (query: string) => {
      if (!agent || !query.trim()) {
        setSearchResults([]);
        return;
      }

      try {
        setSearching(true);
        const response = await agent.client.call(
          place.stream.live.searchActorsTypeahead,
          {
            q: query,
            limit: 10,
          },
        );

        setSearchResults(
          response.actors.map((actor: any) => ({
            did: actor.did,
            handle: actor.handle,
          })),
        );
      } catch (error: any) {
        console.error("Failed to search actors:", error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    },
    [agent],
  );

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);

    // Clear previous timeout
    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout);
    }

    // Set new timeout for debounced search
    if (query.trim()) {
      const timeout = setTimeout(() => {
        searchActors(query);
      }, 300);
      setSearchDebounceTimeout(timeout);
    } else {
      setSearchResults([]);
    }
  };

  const handleSelectActor = async (actor: ActorSearchResult) => {
    if (streamers.length >= 8) {
      a.show("Maximum Reached", "You can only add up to 8 recommendations.");
      return;
    }

    if (streamers.includes(actor.did)) {
      a.show(
        "Already Added",
        "This streamer is already in your recommendations.",
      );
      return;
    }

    const newStreamers = [...streamers, actor.did];
    await saveRecommendations(newStreamers);

    // Clear search
    setSearchQuery("");
    setSearchResults([]);
  };

  const validateDID = (did: string, index: number): boolean => {
    const trimmed = did.trim();
    if (!trimmed) {
      setErrors((prev) => ({ ...prev, [index]: "DID is required" }));
      return false;
    }
    if (!trimmed.startsWith("did:")) {
      setErrors((prev) => ({
        ...prev,
        [index]: "DID must start with 'did:'",
      }));
      return false;
    }
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
    return true;
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditValue(streamers[index]);
    setErrors({});
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue("");
    setErrors({});
  };

  const handleSaveEdit = async () => {
    if (editingIndex === null) return;

    const trimmed = editValue.trim();
    if (!trimmed) {
      setErrors({ [editingIndex]: "DID is required" });
      return;
    }
    if (!trimmed.startsWith("did:")) {
      setErrors({ [editingIndex]: "DID must start with 'did:'" });
      return;
    }

    const newStreamers = [...streamers];
    newStreamers[editingIndex] = trimmed;
    await saveRecommendations(newStreamers);
    setEditingIndex(null);
    setEditValue("");
    setErrors({});
  };

  const handleAddRecommendation = () => {
    if (streamers.length >= 8) {
      a.show("Maximum Reached", "You can only add up to 8 recommendations.");
      return;
    }
    const newIndex = streamers.length;
    setStreamers([...streamers, ""]);
    setEditingIndex(newIndex);
    setEditValue("");
  };

  const handleDelete = (index: number) => {
    setDeleteDialog({ isVisible: true, index });
  };

  const confirmDelete = async () => {
    if (deleteDialog.index === null) return;

    const newStreamers = streamers.filter((_, i) => i !== deleteDialog.index);
    await saveRecommendations(newStreamers);
    setDeleteDialog({ isVisible: false, index: null });
  };

  useEffect(() => {
    if (!agent) return;
    loadRecommendations();
  }, [agent]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout);
      }
    };
  }, [searchDebounceTimeout]);

  if (!agent) {
    return <Loading />;
  }

  return (
    <>
      <ScrollView contentContainerStyle={{ alignItems: "center" }}>
        <View
          style={[{ maxWidth: 800, width: "100%" }, zero.px[8], zero.py[6]]}
        >
          <SettingsViewHeader
            title={t("recommendations-to-others")}
            description={t("recommendations-description")}
            action={
              <Button
                size="sm"
                variant="secondary"
                width="min"
                leftIcon={<RefreshCw size={16} />}
                onPress={loadRecommendations}
                disabled={loading || saving}
              >
                {t("refresh")}
              </Button>
            }
          />

          {/* Search Bar */}
          {streamers.length < 8 && (
            <MenuContainer>
              <MenuGroup>
                <View style={[px[3], py[2]]}>
                  <View
                    style={[
                      layout.flex.row,
                      layout.flex.alignCenter,
                      gap.all[2],
                    ]}
                  >
                    <Search size={18} color={theme.colors.text3} />
                    <Input
                      value={searchQuery}
                      onChangeText={handleSearchChange}
                      placeholder="Search for streamers..."
                    />
                  </View>
                </View>

                {searching && (
                  <>
                    <MenuSeparator />
                    <View style={[py[2], layout.flex.center]}>
                      <Text size="sm" style={{ color: theme.colors.text3 }}>
                        Searching...
                      </Text>
                    </View>
                  </>
                )}

                {!searching && searchResults.length > 0 && (
                  <>
                    <MenuSeparator />
                    {searchResults.map((actor, index) => {
                      const alreadyAdded = streamers.includes(actor.did);
                      return (
                        <View key={actor.did}>
                          {index > 0 && <MenuSeparator />}
                          <Pressable
                            onPress={() =>
                              !alreadyAdded && handleSelectActor(actor)
                            }
                            disabled={alreadyAdded}
                          >
                            {({ pressed }) => (
                              <View
                                style={[
                                  px[3],
                                  py[2],
                                  layout.flex.row,
                                  layout.flex.alignCenter,
                                  gap.all[2],
                                  r.md,
                                  {
                                    backgroundColor:
                                      pressed && !alreadyAdded
                                        ? theme.colors.surface3
                                        : "transparent",
                                    opacity: alreadyAdded ? 0.5 : 1,
                                  },
                                ]}
                              >
                                <View style={{ flex: 1 }}>
                                  <Text>@{actor.handle}</Text>
                                </View>
                                {alreadyAdded && (
                                  <Text
                                    size="xs"
                                    style={{ color: theme.colors.text3 }}
                                  >
                                    Added
                                  </Text>
                                )}
                              </View>
                            )}
                          </Pressable>
                        </View>
                      );
                    })}
                  </>
                )}

                {!searching &&
                  searchQuery.trim() &&
                  searchResults.length === 0 && (
                    <>
                      <MenuSeparator />
                      <View style={[py[2], layout.flex.center]}>
                        <Text size="sm" style={{ color: theme.colors.text3 }}>
                          No results found
                        </Text>
                      </View>
                    </>
                  )}
              </MenuGroup>

              {searchQuery.trim() === "" && (
                <MenuInfo description="Search for streamers by handle or name, or enter DIDs manually below" />
              )}
            </MenuContainer>
          )}

          {loading ? (
            <Loading />
          ) : (
            <MenuContainer>
              <MenuGroup>
                {streamers.length === 0 ? (
                  <EmptyState
                    illustration={<EmptyStateTile icon={Users} />}
                    title={t("no-recommendations-yet")}
                    subtitle="Search above to add streamers you want to recommend to your viewers."
                  />
                ) : (
                  <Sortable.Grid
                    columns={1}
                    activeItemOpacity={90}
                    activeItemScale={1}
                    onActiveItemDropped={() => {
                      saveRecommendations(streamers);
                    }}
                    data={streamers}
                    keyExtractor={(item: string) => `item-${item}`}
                    overDrag="vertical"
                    onDragStart={(e) => {
                      console.log("dragging", e.key);
                      setActiveDrag(e.key);
                    }}
                    onDragEnd={() => setActiveDrag("")}
                    renderItem={(params: any) => {
                      const streamer: string = params.item;
                      const index: number = params.index ?? 0;
                      const beforeSeparator =
                        index > 0 && "item-" + params.item !== activeDrag ? (
                          <MenuSeparator key={`sep-${index}`} />
                        ) : null;

                      return (
                        <>
                          {beforeSeparator}
                          <MenuItem key={`item-${index}`}>
                            <GripVertical
                              color={theme.colors.text3}
                              size={18}
                              style={{
                                marginLeft: -4,
                                marginRight: 4,
                              }}
                            />
                            {editingIndex === index ? (
                              <>
                                <View style={{ flex: 1 }}>
                                  <Input
                                    value={editValue}
                                    onChangeText={setEditValue}
                                    placeholder="did:plc:..."
                                    autoFocus
                                  />
                                  {errors[index] && (
                                    <Text
                                      size="xs"
                                      style={{
                                        color: theme.colors.danger,
                                        marginTop: 4,
                                      }}
                                    >
                                      {errors[index]}
                                    </Text>
                                  )}
                                </View>

                                <IconButton
                                  size="sm"
                                  variant="ghost"
                                  accessibilityLabel="Save"
                                  onPress={handleSaveEdit}
                                >
                                  <Check size={18} color={theme.colors.text1} />
                                </IconButton>

                                <IconButton
                                  size="sm"
                                  variant="ghost"
                                  accessibilityLabel="Cancel"
                                  onPress={handleCancelEdit}
                                >
                                  <X size={18} color={theme.colors.text3} />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <View style={{ flex: 1 }}>
                                  <Text
                                    numberOfLines={1}
                                    ellipsizeMode="middle"
                                  >
                                    {streamer || "(empty)"}
                                  </Text>
                                </View>

                                <IconButton
                                  size="sm"
                                  variant="ghost"
                                  accessibilityLabel="Edit"
                                  onPress={() => handleEdit(index)}
                                >
                                  <Pencil
                                    size={18}
                                    color={theme.colors.text3}
                                  />
                                </IconButton>

                                <IconButton
                                  size="sm"
                                  variant="ghost"
                                  accessibilityLabel="Delete"
                                  onPress={() => handleDelete(index)}
                                >
                                  <X size={18} color={theme.colors.danger} />
                                </IconButton>
                              </>
                            )}
                          </MenuItem>
                        </>
                      );
                    }}
                    onOrderChange={(params) => {
                      console.log(params);
                      // calculate new order from params
                      // duplicate streamers array
                      const newData = [...streamers];
                      const movedItem = newData.splice(params.fromIndex, 1)[0];
                      newData.splice(params.toIndex, 0, movedItem);
                      setStreamers(newData);
                    }}
                    rowGap={0}
                    columnGap={0}
                  />
                )}

                {streamers.length > 0 && streamers.length < 8 && (
                  <MenuSeparator />
                )}

                {streamers.length < 8 && (
                  <SettingsRowItem onPress={handleAddRecommendation}>
                    <View
                      style={[
                        layout.flex.row,
                        layout.flex.alignCenter,
                        gap.all[2],
                      ]}
                    >
                      <Plus size={18} color={theme.colors.text1} />
                      <Text>Add DID manually</Text>
                    </View>
                  </SettingsRowItem>
                )}

                {saving && (
                  <View style={[mt[2], layout.flex.center]}>
                    <Text size="sm" style={{ color: theme.colors.text3 }}>
                      {t("saving")}
                    </Text>
                  </View>
                )}
              </MenuGroup>
            </MenuContainer>
          )}
        </View>
      </ScrollView>

      <ResponsiveDialog
        open={deleteDialog.isVisible}
        onOpenChange={(open) =>
          !open && setDeleteDialog({ isVisible: false, index: null })
        }
        title={t("delete")}
        dismissible={true}
        size="sm"
      >
        <View style={[w.percent[100], gap.all[2], mb[4]]}>
          <Text size="2xl">{t("confirm-delete")}</Text>
          <Text>{t("action-cannot-be-undone")}</Text>
        </View>

        <View style={[layout.flex.row, layout.flex.justify.end, gap.all[3]]}>
          <Button
            variant="secondary"
            width="min"
            onPress={() => setDeleteDialog({ isVisible: false, index: null })}
            disabled={saving}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="danger"
            width="min"
            onPress={confirmDelete}
            disabled={saving}
          >
            {saving ? t("deleting") : t("delete")}
          </Button>
        </View>
      </ResponsiveDialog>
    </>
  );
}
